import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { mergeTemperatureFamilyData, resolveTemperatureTemplate } from './temperature-calibration-profile';
import {
  certificateImageAsDataUrl,
  drawCertificateFooter,
  drawCertificateHeader,
  drawCertificateLegalText,
  drawReferenceStandardsTable,
} from './certificate-pdf-layout';

const LOGO_URL = '/logo-calins-color-hd.png';
const SIGNATURE_URL = new URL('../assets/firma-daniel-kemeny.png', import.meta.url).href;

function numberOrNull(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function fmt(value, decimals = 3) {
  const number = numberOrNull(value);
  return number === null ? '—' : number.toFixed(decimals).replace('.', ',');
}

function fmtPct(value, decimals = 2) {
  const number = numberOrNull(value);
  return number === null ? '—' : `${number.toFixed(decimals).replace('.', ',')} %`;
}

function dmy(value) {
  if (!value) return '—';
  const text = String(value);
  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(text) ? `${text}T12:00:00` : text);
  if (Number.isNaN(date.getTime())) return '—';
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
}

function certificateNumber(...values) {
  for (const value of values) {
    const text = String(value ?? '').trim();
    if (/^\d+$/.test(text)) return text;
    const match = text.match(/OS-(\d+)-(\d+)$/i);
    if (match) return `${match[1]}${match[2]}`;
  }
  return '—';
}

async function imageAsDataUrl(url) {
  return certificateImageAsDataUrl(url);
}

function header(doc, { logo, number, page, pages, first = false }) {
  drawCertificateHeader(doc, { logo, number, page, pages, first });
}

function footer(doc, page, pages) {
  drawCertificateFooter(doc, page, pages);
}

function legalText(doc, y) {
  return drawCertificateLegalText(doc, y);
}

function labelValue(doc, label, value, x, y, valueX = x + 42) {
  doc.setFont('helvetica', 'bold');
  doc.text(label, x, y);
  doc.setFont('helvetica', 'normal');
  doc.text(String(value ?? '—'), valueX, y);
}

function profileConfigurationLines(familyData = {}, template = {}) {
  if (template.profileKey === 'infrared_thermometer') {
    const data = familyData.infrared || {};
    return [
      `Emisividad: ${data.emissivity ?? '—'} - Distancia: ${data.measurement_distance_mm ?? '—'} mm - Diámetro del punto: ${data.spot_diameter_mm ?? '—'} mm`,
      `Superficie objetivo: ${data.target_surface || '—'}`,
    ];
  }
  if (template.profileKey === 'rtd_thermocouple') {
    const data = familyData.sensor || {};
    return [
      `Sensor: ${data.sensor_type || '—'} - Conexión: ${data.connection_wires || '—'} - Junta fría: ${data.cold_junction_compensation || '—'}`,
      `Profundidad de inmersión: ${data.immersion_depth_mm ?? '—'} mm`,
    ];
  }
  if (template.profileKey === 'temperature_chamber') {
    const data = familyData.chamber || {};
    return [
      `Recinto: ${data.chamber_type || '—'} - Carga: ${data.load_condition || '—'} - Estabilización: ${data.stabilization_time_min ?? '—'} min`,
      `Posición: ${data.measurement_position || '—'} - Puerta: ${data.door_condition || '—'}`,
    ];
  }
  return [];
}

function profileBudgetOrigin(familyData = {}, template = {}) {
  const budget = familyData.standard_budget || {};
  if (template.profileKey === 'infrared_thermometer') {
    return `Emisividad ${fmt(budget.emissivity_uncertainty_c)} °C; distancia/punto ${fmt(budget.distance_uncertainty_c)} °C`;
  }
  if (template.profileKey === 'rtd_thermocouple') {
    return `Sensor/adaptación ${fmt(budget.sensor_uncertainty_c)} °C; junta fría ${fmt(budget.cold_junction_uncertainty_c)} °C`;
  }
  if (template.profileKey === 'temperature_chamber') {
    return `Estabilidad ${fmt(budget.stability_uncertainty_c)} °C; uniformidad ${fmt(budget.uniformity_uncertainty_c)} °C`;
  }
  return 'Evaluación del método';
}

function drawPageOne(doc, { session, instrument, familyData }) {
  const template = resolveTemperatureTemplate(session.template_code || familyData.profile_key);
  let y = legalText(doc, 66) + 3;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('ESPECIFICACIONES', 14, y);
  y += 8;
  doc.setFontSize(9.5);

  const left = [
    ['Objeto:', session.instrument_object || instrument?.type || 'Termómetro'],
    ['Fabricante:', session.instrument_brand || instrument?.brand || '—'],
    ['Modelo:', session.instrument_model || instrument?.model || '—'],
    ['N.º de serie:', session.instrument_serial || instrument?.serial || '—'],
    ['TAG:', session.instrument_tag || instrument?.tag || '—'],
    ['Intervalo:', `${fmt(session.instrument_range_min, 2)} a ${fmt(session.instrument_range_max, 2)} °C`],
  ];
  const right = [
    ['Determinación:', template.shortTitle || 'Calibración de temperatura'],
    ['Resolución:', `${fmt(session.instrument_resolution, 3)} °C`],
    ['Clase / tolerancia:', session.instrument_class == null ? '—' : `${fmt(session.instrument_class)} °C`],
    ['Fecha de calibración:', dmy(session.calibration_date)],
    ['Fecha de emisión:', dmy(session.report_issue_date)],
    ['Válido hasta:', dmy(session.valid_until)],
  ];
  left.forEach(([label, value], index) => labelValue(doc, label, value, 14, y + index * 5.5, 48));
  right.forEach(([label, value], index) => labelValue(doc, label, value, 111, y + index * 5.5, 151));
  y += Math.max(left.length, right.length) * 5.5 + 9;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('CLIENTE', 14, y);
  y += 8;
  doc.setFontSize(9.5);
  [
    ['Razón social:', session.client_name],
    ['Dirección:', session.client_address],
    ['Ciudad:', session.client_city],
    ['Provincia:', session.client_province],
  ].forEach(([label, value], index) => labelValue(doc, label, value || '—', 26, y + index * 5.5, 58));
  y += 31;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('CONDICIONES AMBIENTALES', 14, y);
  y += 7;
  autoTable(doc, {
    startY: y,
    margin: { left: 28, right: 28 },
    theme: 'grid',
    styles: { fontSize: 9, halign: 'center', cellPadding: 2, lineWidth: 0.1, lineColor: [70, 70, 70] },
    headStyles: { fillColor: [180, 215, 225], textColor: [0, 0, 0], fontStyle: 'bold' },
    head: [['Temperatura ambiente', 'Presión atmosférica', 'Humedad relativa']],
    body: [[
      `${fmt(session.ambient_temp_avg, 1)} °C`,
      `${fmt(session.ambient_pressure_avg, 1)} hPa`,
      `${fmt(session.ambient_humidity_avg, 1)} %HR`,
    ]],
  });

  const readings = familyData.environment_readings || [];
  if (readings.some((reading) => numberOrNull(reading.temp_c) !== null)) {
    autoTable(doc, {
      startY: (doc.lastAutoTable?.finalY || y) + 6,
      margin: { left: 28, right: 28 },
      theme: 'grid',
      styles: { fontSize: 8.5, halign: 'center', cellPadding: 1.8, lineWidth: 0.1 },
      headStyles: { fillColor: [180, 215, 225], textColor: [0, 0, 0], fontStyle: 'bold' },
      head: [['Momento', 'Temperatura (°C)', 'Presión (hPa)', 'Humedad (%HR)']],
      body: readings.map((reading) => [
        reading.label,
        fmt(reading.temp_c, 1),
        fmt(reading.pressure_hpa, 1),
        fmt(reading.humidity_rh, 1),
      ]),
    });
  }
}

function groupRawPoints(points = []) {
  const rows = {};
  const keyMap = {
    '1:ASC': ['s1aPattern', 's1aInstrument'],
    '1:DESC': ['s1dPattern', 's1dInstrument'],
    '2:ASC': ['s2aPattern', 's2aInstrument'],
    '2:DESC': ['s2dPattern', 's2dInstrument'],
  };
  points.forEach((point) => {
    const pointNo = Number(point.point_no);
    const row = rows[pointNo] || { pointNo, setpoint: point.setpoint };
    const keys = keyMap[`${Number(point.series_no)}:${String(point.direction || '').toUpperCase()}`];
    if (keys) {
      row[keys[0]] = point.pattern_reading;
      row[keys[1]] = point.instrument_reading;
    }
    rows[pointNo] = row;
  });
  return Object.values(rows).sort((a, b) => a.pointNo - b.pointNo);
}

function drawPageTwo(doc, { session, points, results }) {
  const template = resolveTemperatureTemplate(session?.template_code);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('RESULTADOS OBTENIDOS DURANTE LA CALIBRACIÓN', 14, 40);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.text(template.title, 14, 45);

  const rawRows = groupRawPoints(points);
  autoTable(doc, {
    startY: 49,
    margin: { left: 10, right: 10 },
    theme: 'grid',
    styles: { fontSize: 6.6, halign: 'center', valign: 'middle', cellPadding: 1.1, lineWidth: 0.1 },
    headStyles: { fillColor: [180, 215, 225], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 6.3 },
    head: [
      [
        { content: 'Pto.', rowSpan: 3 },
        { content: 'Teórico', rowSpan: 3 },
        { content: '1ª serie', colSpan: 4 },
        { content: '2ª serie', colSpan: 4 },
      ],
      [
        { content: 'Ascendente', colSpan: 2 },
        { content: 'Descendente', colSpan: 2 },
        { content: 'Ascendente', colSpan: 2 },
        { content: 'Descendente', colSpan: 2 },
      ],
      ['Patrón', 'Instrumento', 'Patrón', 'Instrumento', 'Patrón', 'Instrumento', 'Patrón', 'Instrumento'],
    ],
    body: rawRows.map((row) => [
      row.pointNo,
      fmt(row.setpoint, 3),
      fmt(row.s1aPattern, 3),
      fmt(row.s1aInstrument, 3),
      fmt(row.s1dPattern, 3),
      fmt(row.s1dInstrument, 3),
      fmt(row.s2aPattern, 3),
      fmt(row.s2aInstrument, 3),
      fmt(row.s2dPattern, 3),
      fmt(row.s2dInstrument, 3),
    ]),
  });

  const sortedResults = [...results].sort((a, b) => a.point_no - b.point_no);
  autoTable(doc, {
    startY: (doc.lastAutoTable?.finalY || 105) + 10,
    margin: { left: 10, right: 10 },
    theme: 'grid',
    styles: { fontSize: 7.3, halign: 'center', cellPadding: 1.5, lineWidth: 0.1 },
    headStyles: { fillColor: [180, 215, 225], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 6.8 },
    head: [[
      'Pto.',
      'Media patrón',
      'Media instrumento',
      'Histéresis',
      'Error',
      'Error %',
      'Repetibilidad',
      'U expandida',
    ]],
    body: sortedResults.map((result) => [
      result.point_no,
      fmt(result.pattern_mean),
      fmt(result.instrument_mean),
      fmt(result.hysteresis),
      fmt(result.error_abs),
      fmtPct(result.error_pct),
      fmt(result.repeatability),
      fmt(result.u_reported),
    ]),
  });

  const maxError = sortedResults.reduce((current, result) => (
    Math.max(current, Math.abs(numberOrNull(result.error_abs) || 0))
  ), 0);
  const maxUncertainty = sortedResults.reduce((current, result) => (
    Math.max(current, Math.abs(numberOrNull(result.u_reported) || 0))
  ), 0);
  const summaryY = (doc.lastAutoTable?.finalY || 170) + 10;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text(`Error sistemático mayor: ${fmt(maxError)} °C`, 18, summaryY);
  doc.text(`Incertidumbre expandida máxima: ${fmt(maxUncertainty)} °C`, 18, summaryY + 6);
}

function drawPageThree(doc, { session, familyData, signature }) {
  const template = resolveTemperatureTemplate(session.template_code || familyData.profile_key);
  let y = 42;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('OBSERVACIONES', 14, y);
  y += 9;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  const uncertaintyText = 'La incertidumbre expandida se calculó multiplicando la incertidumbre estándar combinada por k=2, correspondiente a un nivel de confianza cercano al 95 %.';
  doc.text(doc.splitTextToSize(uncertaintyText, 180), 14, y);
  y += 17;
  if (session.notes) {
    doc.text(doc.splitTextToSize(String(session.notes), 180), 14, y);
    y += 18;
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('MÉTODO Y PROCEDIMIENTO', 14, y);
  y += 8;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.text(familyData.method || 'Por comparación con instrumento patrón.', 14, y);
  y += 6;
  doc.text(familyData.procedure || 'P-DT-PT-07-02 (Calibración de termómetros)', 14, y);
  y += 6;
  const configurationLines = profileConfigurationLines(familyData, template);
  if (configurationLines.length) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.text(configurationLines, 14, y);
    y += configurationLines.length * 4 + 7;
  } else {
    y += 8;
  }

  const configuration = familyData.standard_configuration || {};
  const roleLabels = {
    source: 'Medio generador',
    reference: 'Referencia',
    environment: 'Ambiente',
  };
  const configuredReferences = ['source', 'reference', 'environment']
    .filter((role) => configuration[role])
    .map((role) => ({ ...configuration[role], usage: roleLabels[role] }));
  drawReferenceStandardsTable(doc, {
    startY: y,
    title: 'PATRONES DE REFERENCIA',
    references: configuredReferences,
  });

  const budget = familyData.standard_budget || {};
  const reference = configuration.reference || {};
  const referenceResults = Array.isArray(reference.results) ? reference.results : [];
  const certificateUMax = referenceResults.reduce((maximum, result) => (
    Math.max(maximum, Math.abs(numberOrNull(result.uncertainty_expanded) || 0))
  ), 0);
  const coverageFactor = referenceResults
    .map((result) => numberOrNull(result.coverage_factor))
    .find((value) => value !== null)
    || numberOrNull(reference?.certificate?.coverage_factor)
    || 2;
  y = (doc.lastAutoTable?.finalY || y) + 10;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('PRESUPUESTO DE INCERTIDUMBRE', 14, y);
  y += 5;
  autoTable(doc, {
    startY: y,
    margin: { left: 14, right: 14 },
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 1.7, lineWidth: 0.1 },
    headStyles: { fillColor: [180, 215, 225], textColor: [0, 0, 0], fontStyle: 'bold' },
    head: [['Contribución', 'Origen', 'Valor (°C)']],
    body: [
      ['u calibración referencia', `U/k · k=${fmt(coverageFactor, 2)}`, fmt(certificateUMax / coverageFactor)],
      ['Resolución de la referencia', 'd / √12', fmt(reference?.capability?.resolution)],
      ['Deriva de la referencia', 'Historial / límite declarado', fmt(budget.drift)],
      ['Específicas / otras', `${profileBudgetOrigin(familyData, template)}; otras ${fmt(budget.other_uncertainty)} °C`, 'Incluidas'],
    ],
  });

  const definitionsY = (doc.lastAutoTable?.finalY || y) + 11;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.text('Error del instrumento', 14, definitionsY);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.2);
  doc.text('Diferencia entre el promedio de las indicaciones del instrumento y el promedio de las indicaciones del patrón.', 14, definitionsY + 5, { maxWidth: 180 });
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.text('Histéresis', 14, definitionsY + 17);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.2);
  doc.text('Máxima diferencia de indicación del instrumento entre las carreras ascendente y descendente para un mismo punto.', 14, definitionsY + 22, { maxWidth: 180 });

  if (signature) doc.addImage(signature, 'PNG', 78, 235, 44, 29, undefined, 'FAST');
}

export async function generateTemperatureNoSacCertificatePdf(
  { session, instrument, points = [], results = [] },
  options = {},
) {
  if (!session) throw new Error('La sesión es requerida.');
  if (!results.length) throw new Error('No hay resultados para certificar.');

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const [logo, signature] = await Promise.all([
    imageAsDataUrl(options.logoUrl || LOGO_URL),
    imageAsDataUrl(options.signatureUrl || SIGNATURE_URL),
  ]);
  const number = certificateNumber(
    options.serviceOrderCode,
    session.service_order_code,
    session.certificate_number,
    instrument?.lab_id,
  );
  const familyData = mergeTemperatureFamilyData(session.family_data, session.template_code);

  drawPageOne(doc, { session, instrument, familyData });
  doc.addPage();
  drawPageTwo(doc, { session, points, results });
  doc.addPage();
  drawPageThree(doc, { session, familyData, signature });

  const pages = doc.getNumberOfPages();
  for (let page = 1; page <= pages; page += 1) {
    doc.setPage(page);
    header(doc, { logo, number, page, pages, first: page === 1 });
    footer(doc, page, pages);
  }

  if (options.returnBlob) {
    return new Blob([doc.output('arraybuffer')], { type: 'application/pdf' });
  }
  doc.save(options.filename || `${number !== '—' ? number : 'R-DT-PC-TEMP'}.pdf`);
  return null;
}

